/**
 * Entity Field and Schema Enums
 *
 * Enums for dynamic entity field types and schema definitions.
 * Issue #313 - Centralize enums to shared-types
 *
 * Note: FormFieldType is defined in contracts/dynamic-ui.contract.ts as an enum.
 * The FieldType here is for entity schema definitions, while FormFieldType
 * is for dynamic form rendering - they serve different purposes but overlap.
 */

import { z } from 'zod';

// ============================================================================
// ENTITY FIELD TYPE
// ============================================================================

/**
 * Field types for entity schema definitions
 * Used for defining custom entity fields in the Entity Framework
 */
export enum EntityFieldType {
  /** Single-line text input */
  TEXT = 'TEXT',
  /** Multi-line text area */
  TEXTAREA = 'TEXTAREA',
  /** Rich text editor with formatting */
  RICH_TEXT = 'RICH_TEXT',
  /** Numeric value */
  NUMBER = 'NUMBER',
  /** Currency amount with formatting */
  CURRENCY = 'CURRENCY',
  /** Percentage value */
  PERCENTAGE = 'PERCENTAGE',
  /** Date only (no time) */
  DATE = 'DATE',
  /** Date and time */
  DATETIME = 'DATETIME',
  /** Time only (no date) */
  TIME = 'TIME',
  /** Boolean true/false */
  BOOLEAN = 'BOOLEAN',
  /** Single selection from options */
  SELECT = 'SELECT',
  /** Multiple selections from options */
  MULTI_SELECT = 'MULTI_SELECT',
  /** Lookup/reference to another entity */
  LOOKUP = 'LOOKUP',
  /** Multi-lookup/reference to multiple entity records */
  MULTI_LOOKUP = 'MULTI_LOOKUP',
  /** Email address */
  EMAIL = 'EMAIL',
  /** Phone number */
  PHONE = 'PHONE',
  /** URL/web address */
  URL = 'URL',
  /** Physical address */
  ADDRESS = 'ADDRESS',
  /** Auto-incrementing number */
  AUTO_NUMBER = 'AUTO_NUMBER',
  /** File attachment */
  FILE = 'FILE',
  /** Image upload */
  IMAGE = 'IMAGE',
  /** JSON data */
  JSON = 'JSON',
  /** Calculated value computed from a formula */
  CALCULATED = 'CALCULATED',
  /** Rollup value aggregated from related records */
  ROLLUP = 'ROLLUP',
  /** Star/numeric rating */
  RATING = 'RATING',
  /** E-signature capture */
  SIGNATURE = 'SIGNATURE',
}

/** Zod schema for EntityFieldType validation */
export const EntityFieldTypeSchema = z.nativeEnum(EntityFieldType);

/** Array of all entity field types for iteration */
export const ENTITY_FIELD_TYPES = Object.values(EntityFieldType);

// ============================================================================
// ENTITY STATUS
// ============================================================================

/**
 * Entity record status (soft delete pattern)
 */
export enum EntityStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ARCHIVED = 'archived',
  DELETED = 'deleted',
}

/** Zod schema for EntityStatus validation */
export const EntityStatusSchema = z.nativeEnum(EntityStatus);

/** Array of all entity statuses for iteration */
export const ENTITY_STATUSES = Object.values(EntityStatus);

// ============================================================================
// SEARCHABLE ENTITY TYPE
// ============================================================================

/**
 * Entity types that can be searched via unified search
 */
export enum SearchableEntityType {
  CASE = 'case',
  CONSUMER = 'consumer',
  USER = 'user',
  BUSINESS = 'business',
  LEAD = 'lead',
  DOCUMENT = 'document',
  PARTNER = 'partner',
}

/** Zod schema for SearchableEntityType validation */
export const SearchableEntityTypeSchema = z.nativeEnum(SearchableEntityType);

/** Array of all searchable entity types for iteration */
export const SEARCHABLE_ENTITY_TYPES = Object.values(SearchableEntityType);

// ============================================================================
// FIELD VALIDATION TYPE
// ============================================================================

/**
 * Built-in validation types for entity fields
 */
export enum FieldValidationType {
  REQUIRED = 'required',
  MIN_LENGTH = 'min_length',
  MAX_LENGTH = 'max_length',
  MIN_VALUE = 'min_value',
  MAX_VALUE = 'max_value',
  PATTERN = 'pattern',
  EMAIL = 'email',
  URL = 'url',
  PHONE = 'phone',
  UNIQUE = 'unique',
}

/** Zod schema for FieldValidationType validation */
export const FieldValidationTypeSchema = z.nativeEnum(FieldValidationType);

/** Array of all field validation types for iteration */
export const FIELD_VALIDATION_TYPES = Object.values(FieldValidationType);
